(() => {
  const MAX_HISTORY = 12;
  const REQUEST_TIMEOUT_MS = 15000;

  class MayaBarista {
    constructor(root) {
      this.root = root;
      this.sessionEndpoint = root.dataset.sessionEndpoint;
      this.chatEndpoint = root.dataset.chatEndpoint;
      this.personalityEndpoint = root.dataset.personalityEndpoint;
      this.form = root.querySelector('[data-maya-form]');
      this.input = root.querySelector('[data-maya-input]');
      this.send = root.querySelector('[data-maya-send]');
      this.reply = root.querySelector('[data-maya-reply]');
      this.choices = root.querySelector('[data-maya-choices]');
      this.progress = root.querySelector('[data-maya-progress]');
      this.recommendation = root.querySelector('[data-maya-recommendation]');
      this.subscriptionOffer = root.querySelector('[data-maya-subscription-offer]');
      this.characterAssets = this.readCharacterAssets();
      this.history = [];
      this.seenProductHandles = new Set();
      this.currentProductHandle = null;
      this.questions = [];
      this.answers = [];
      this.questionIndex = 0;
      this.mode = null;
      this.personalityComplete = false;
      this.personalityResult = null;
      this.sessionToken = null;
      this.shareObjectUrl = null;
      this.busy = false;
      this.initialReply = this.reply.textContent;
      this.bind();
      this.startSession();
    }

    // Anonymous funnel count: event name and result type only.
    track(name, type) {
      try {
        const url = new URL('/api/event', this.personalityEndpoint || location.href).href;
        const body = JSON.stringify({ event: name, type: type || '' });
        const sent = navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
        if (!sent) fetch(url, { method: 'POST', mode: 'cors', keepalive: true, headers: { 'Content-Type': 'text/plain' }, body }).catch(() => {});
      } catch (_) {}
    }

    bind() {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit(this.input.value);
      });

      this.choices.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.dataset.mode) this.chooseMode(button.dataset.mode);
        else if (button.dataset.optionId && !this.personalityComplete) this.answerPersonality(button.dataset.optionId);
        else if (button.dataset.value) this.submit(button.dataset.value);
      });

      this.root.querySelector('[data-maya-restart]').addEventListener('click', () => this.reset());
    }

    async startSession() {
      try {
        const diagnosis = await this.requestGet(this.personalityEndpoint);
        this.questions = diagnosis.questions || [];
        if (!this.questions.length) throw new Error('Questions are unavailable');
        this.form.hidden = true;
        this.renderModeChoice();

        // A temporary session failure must not prevent the public quiz from starting.
        // The session is retried immediately before the result is requested.
        try {
          await this.ensureSession();
        } catch (_) {
          this.sessionToken = null;
        }
      } catch (_) {
        this.setReply('少しだけ準備に時間がかかっています。もう一度お試しください。');
      }
    }

    async requestGet(url) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(url, { mode: 'cors', signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
      } finally {
        clearTimeout(timer);
      }
    }

    async ensureSession() {
      if (this.sessionToken) return this.sessionToken;
      const session = await this.request(this.sessionEndpoint, {});
      this.sessionToken = session.sessionToken;
      if (!this.sessionToken) throw new Error('Session token is unavailable');
      return this.sessionToken;
    }

    readCharacterAssets() {
      try {
        return JSON.parse(this.root.querySelector('[data-maya-character-map]')?.textContent || '{}');
      } catch (_) {
        return {};
      }
    }

    renderModeChoice() {
      this.mode = null;
      this.progress.hidden = true;
      this.form.hidden = false;
      this.input.placeholder = 'まやに質問する';
      this.input.setAttribute('aria-label', 'まやへの質問');
      if (!this.chatIntro) {
        this.chatIntro = document.createElement('div');
        this.chatIntro.className = 'maya-chat-intro';
        const heading = document.createElement('p');
        heading.textContent = 'まやがコーヒーの相談にお答えします';
        const examples = document.createElement('p');
        const full = document.createElement('span');
        full.className = 'maya-chat-example--full';
        full.textContent = '例えば「深煎りのおすすめは？」「ンゴロンゴロのリンクを教えて」';
        const compact = document.createElement('span');
        compact.className = 'maya-chat-example--compact';
        compact.textContent = '例「深煎りのおすすめは？」「豆のリンクは？」';
        examples.append(full, compact);
        this.chatIntro.append(heading, examples);
        this.form.after(this.chatIntro);
        this.root.querySelector('.maya-barista__notice')?.remove();
      }
      this.chatIntro.hidden = false;
      this.setReply('いらっしゃいませ♪ 診断を楽しむのも、まやに直接質問するのも大歓迎です。今日は何を知りたいですか？');
      this.choices.classList.add('maya-choices--mode');
      this.choices.replaceChildren();
      [
        { id: 'recommendation', title: 'ぴったりの一杯診断', description: '味や焙煎度、飲み方からコーヒーを探す' },
        { id: 'personality', title: 'コーヒー性格診断', description: '8つの質問から、あなたのタイプを見つける' }
      ].forEach((item) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.mode = item.id;
        const title = document.createElement('strong');
        title.textContent = item.title;
        const description = document.createElement('small');
        description.textContent = item.description;
        button.append(title, description);
        this.choices.append(button);
      });
    }

    chooseMode(mode) {
      if (this.busy) return;
      if (this.chatIntro) this.chatIntro.hidden = true;
      this.mode = mode;
      this.choices.classList.remove('maya-choices--mode');
      if (mode === 'personality') {
        this.answers = [];
        this.questionIndex = 0;
        this.personalityResult = null;
        this.personalityComplete = false;
        this.form.hidden = true;
        this.track('personality_start');
        this.renderPersonalityQuestion();
        return;
      }

      this.personalityComplete = true;
      this.track('chat_start');
      this.form.hidden = false;
      this.input.placeholder = '好みを言葉で伝えても大丈夫です';
      this.setReply('では、今の気分や好きな味を教えてください。まだ好みが分からなくても、まやが一緒に探します♪');
      this.renderProgress({ current: 1, total: 4, label: '好みを探しています' });
      this.renderChoices(['すっきり・華やかな味', 'コクのある深煎り', 'ミルクと合わせたい', '贈り物を探している']);
    }

    renderPersonalityQuestion() {
      this.choices.classList.remove('maya-choices--mode');
      const question = this.questions[this.questionIndex];
      if (!question) return;
      this.setReply(question.text);
      this.renderProgress({ current: this.questionIndex + 1, total: this.questions.length, label: 'あなたらしさを探索中' });
      this.choices.replaceChildren();
      question.options.forEach((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.optionId = option.id;
        button.textContent = option.label;
        this.choices.append(button);
      });
    }

    async answerPersonality(optionId) {
      if (this.busy) return;
      const question = this.questions[this.questionIndex];
      if (!question?.options.some((option) => option.id === optionId)) return;
      this.answers.push({ questionId: question.id, optionId });
      this.questionIndex += 1;
      if (this.questionIndex < this.questions.length) {
        this.renderPersonalityQuestion();
        return;
      }
      this.setBusy(true);
      this.setReply('少しお待ちくださいね♪ コーヒー性格診断の結果をまとめています。');
      this.choices.replaceChildren();
      try {
        await this.ensureSession();
        const result = await this.request(this.personalityEndpoint, { sessionToken: this.sessionToken, answers: this.answers });
        this.personalityComplete = true;
        this.renderPersonalityResult(result);
      } catch (_) {
        this.questionIndex = Math.max(0, this.questions.length - 1);
        this.answers.pop();
        this.setReply('結果をまとめる途中で通信が途切れました。最後の質問をもう一度お願いします。');
        this.renderPersonalityQuestion();
      } finally {
        this.setBusy(false);
      }
    }

    renderPersonalityResult(result) {
      const type = result.type || {};
      this.personalityResult = result;
      this.track('personality_complete', type.id);
      this.rememberProduct(result.recommendation);
      this.history = [{ role: 'model', text: `コーヒー性格診断の結果は「${type.name}」。${type.introduction || ''}${result.recommendation?.title ? `\n[表示した商品: ${result.recommendation.title}]` : ''}` }];
      this.setReply(type.hidden ? 'コーヒー性格診断の結果が出ました。特別タイプです♪' : 'コーヒー性格診断の結果が出ました♪');
      this.renderProgress({ current: this.questions.length, total: this.questions.length, label: '診断結果' });
      this.choices.replaceChildren();
      this.recommendation.hidden = false;
      this.recommendation.classList.add('maya-recommendation--profile');
      this.recommendation.replaceChildren();

      const profile = document.createElement('div');
      profile.className = 'maya-profile-result';
      const eyebrow = document.createElement('p');
      eyebrow.className = 'maya-profile-result__eyebrow';
      eyebrow.textContent = type.hidden ? 'コーヒー性格診断結果・特別タイプ' : 'コーヒー性格診断結果';
      const title = document.createElement('h2');
      title.textContent = type.name || '診断結果';
      const headline = document.createElement('p');
      headline.className = 'maya-profile-result__headline';
      headline.textContent = type.headline || '';
      const introduction = document.createElement('p');
      introduction.className = 'maya-profile-result__introduction';
      introduction.textContent = type.introduction || '';
      const profileHeader = document.createElement('div');
      profileHeader.className = 'maya-profile-result__header';
      const profileCopy = document.createElement('div');
      profileCopy.append(eyebrow, title, headline);
      const characterUrl = this.resolveCharacterUrl(type);
      if (characterUrl) {
        const characterFrame = document.createElement('div');
        characterFrame.className = 'maya-profile-result__character-frame';
        characterFrame.dataset.type = type.id || '';
        const character = document.createElement('img');
        character.className = 'maya-profile-result__character';
        character.src = characterUrl;
        character.alt = `${type.name || 'コーヒータイプ'}のキャラクター`;
        character.loading = 'eager';
        characterFrame.append(character);
        profileHeader.append(characterFrame);
      }
      profileHeader.append(profileCopy);
      profile.append(profileHeader, introduction);
      if (type.social) {
        const guide = document.createElement('div');
        guide.className = 'maya-personality-mini';
        for (const [label, value] of [['うれしいこと', type.social.joy], ['大切な時間', type.social.moment]]) {
          const row = document.createElement('p');
          const strong = document.createElement('strong');
          strong.textContent = `${label}：`;
          row.append(strong, document.createTextNode(value || ''));
          guide.append(row);
        }
        profile.append(guide);
      }

      const axes = document.createElement('div');
      axes.className = 'maya-profile-axes';
      (result.axes || []).forEach((axis) => {
        const row = document.createElement('div');
        row.className = 'maya-profile-axis';
        const label = document.createElement('span');
        label.textContent = axis.label;
        const value = document.createElement('b');
        value.textContent = `${axis.value}`;
        const track = document.createElement('i');
        const bar = document.createElement('em');
        bar.style.width = `${Math.max(0, Math.min(100, axis.value))}%`;
        track.append(bar);
        row.append(label, value, track);
        axes.append(row);
      });
      profile.append(axes);
      if (result.firstCup) profile.append(this.renderFirstCup(result.firstCup));

      profile.append(this.renderShareCard(result, characterUrl));
      if (result.lineProfile?.loginUrl) profile.append(this.renderPersonalityLineProfile(result.lineProfile));
      this.recommendation.append(profile);

      if (result.recommendation) this.renderProductInside(this.recommendation, result.recommendation);
      this.renderSubscriptionOffer(result.subscriptionOffer || null);
      this.form.hidden = false;
      this.input.placeholder = '結果について、まやに相談できます';
    }

    renderShareCard(result, characterUrl) {
      const type = result.type || {};
      const section = document.createElement('section');
      section.className = 'maya-share-card';
      const heading = document.createElement('div');
      heading.className = 'maya-share-card__heading';
      const eyebrow = document.createElement('p');
      eyebrow.textContent = 'コーヒー性格診断結果をシェア';
      const title = document.createElement('h3');
      title.textContent = 'これ、私っぽい？';
      const description = document.createElement('p');
      description.textContent = '当たっていても、ちょっと意外でも。友達に聞いてみませんか？ シェアはお好みで。';
      heading.append(eyebrow, title, description);

      const preview = document.createElement('img');
      preview.className = 'maya-share-card__preview';
      preview.alt = `${type.name || 'コーヒータイプ'}のシェア画像`;
      preview.hidden = true;

      const status = document.createElement('p');
      status.className = 'maya-share-card__status';
      status.textContent = 'シェア画像を準備しています…';

      const actions = document.createElement('div');
      actions.className = 'maya-profile-actions maya-share-card__actions';
      const nativeShare = document.createElement('button');
      nativeShare.type = 'button';
      nativeShare.disabled = true;
      nativeShare.textContent = '画像を準備中';
      const save = document.createElement('button');
      save.type = 'button';
      save.disabled = true;
      save.textContent = '画像を保存';
      let selectedShareText = result.share?.text || '';
      const variants = document.createElement('div');
      variants.className = 'maya-share-variants';
      variants.setAttribute('role', 'group');
      variants.setAttribute('aria-label', '投稿文の雰囲気を選ぶ');
      const postPreview = document.createElement('p');
      postPreview.className = 'maya-share-post-preview';
      postPreview.setAttribute('aria-live', 'polite');
      postPreview.textContent = selectedShareText;
      const xShare = document.createElement('a');
      const updatePost = () => {
        xShare.href = `https://x.com/intent/post?text=${encodeURIComponent(selectedShareText)}&url=${encodeURIComponent(result.share?.url || location.href)}`;
        postPreview.textContent = selectedShareText;
      };
      updatePost();
      xShare.target = '_blank';
      xShare.rel = 'noopener';
      xShare.textContent = 'Xで文章をシェア';
      const copy = document.createElement('button');
      copy.type = 'button';
      copy.textContent = '文章をコピー';
      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(`${selectedShareText}\n${result.share?.url || location.href}`);
          copy.textContent = 'コピーしました';
        } catch (_) {
          copy.textContent = 'コピーできませんでした';
        }
      });
      actions.append(nativeShare, save, xShare, copy);
      xShare.addEventListener('click', () => this.track('share_x', type.id));
      copy.addEventListener('click', () => this.track('share_copy', type.id));
      save.addEventListener('click', () => this.track('share_save', type.id));
      nativeShare.addEventListener('click', () => this.track('share_native', type.id));
      (result.share?.variants || []).forEach((variant, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = variant.label;
        button.setAttribute('aria-pressed', String(index === 0));
        button.addEventListener('click', () => {
          selectedShareText = variant.text;
          variants.querySelectorAll('button').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
          copy.textContent = '文章をコピー';
          updatePost();
        });
        variants.append(button);
      });
      section.append(heading, preview, status, variants, postPreview, actions);

      this.createShareImage(result, characterUrl).then(({ blob, filename }) => {
        if (this.personalityResult !== result || !section.isConnected) return;
        if (this.shareObjectUrl) URL.revokeObjectURL(this.shareObjectUrl);
        this.shareObjectUrl = URL.createObjectURL(blob);
        preview.src = this.shareObjectUrl;
        preview.hidden = false;
        status.textContent = '画像を保存してInstagramへ。Xには保存した画像を添付して投稿できます。';
        save.disabled = false;
        nativeShare.disabled = false;
        nativeShare.textContent = '画像をシェア';
        save.addEventListener('click', async () => {
          const file = new File([blob], filename, { type: 'image/png' });
          if (this.isAppleMobile() && this.canShareFile(file)) {
            try {
              await navigator.share({ files: [file], title: '私のAIコーヒー性格診断' });
              status.textContent = '共有メニューから「画像を保存」を選ぶと、写真アプリへ保存できます。';
              return;
            } catch (error) {
              if (error?.name === 'AbortError') return;
            }
          }

          if (this.isAppleMobile()) {
            this.openShareImage(blob);
            status.textContent = '画像を開きました。画像を長押しして「写真に保存」を選んでください。';
            return;
          }

          this.downloadShareImage(blob, filename);
          status.textContent = '画像の保存を開始しました。';
        });
        nativeShare.addEventListener('click', async () => {
          const file = new File([blob], filename, { type: 'image/png' });
          if (this.canShareFile(file)) {
            try {
              await navigator.share({
                files: [file],
                title: '私のAIコーヒー性格診断',
                text: selectedShareText
              });
              return;
            } catch (error) {
              if (error?.name === 'AbortError') return;
            }
          }
          if (this.isAppleMobile()) {
            this.openShareImage(blob);
            status.textContent = '画像を開きました。画像を長押しして「写真に保存」を選んでください。';
            return;
          }
          this.downloadShareImage(blob, filename);
          nativeShare.textContent = '画像の保存を開始しました';
        });
      }).catch((error) => {
        console.error('Share image generation failed', error);
        status.textContent = '画像の準備に失敗しました。ページを再読み込みしてお試しください。';
        nativeShare.textContent = '画像を準備できませんでした';
      });
      return section;
    }

    renderFirstCup(cup) {
      const section = document.createElement('section');
      section.className = 'maya-first-cup';
      const eyebrow = document.createElement('p');
      eyebrow.className = 'maya-first-cup__eyebrow';
      eyebrow.textContent = 'あなたのタイプの、最初の一杯';
      const title = document.createElement('h3');
      title.textContent = `${cup.roast || ''} × ${cup.recipe || ''}`;
      const reason = document.createElement('p');
      reason.textContent = cup.reason || '';
      const steps = document.createElement('p');
      steps.className = 'maya-first-cup__steps';
      steps.textContent = cup.steps || '';
      section.append(eyebrow, title, reason, steps);
      return section;
    }

    renderPersonalityLineProfile(lineProfile) {
      const section = document.createElement('section');
      section.className = 'maya-personality-line';
      const eyebrow = document.createElement('p');
      eyebrow.className = 'maya-personality-line__eyebrow';
      eyebrow.textContent = 'もっと知りたい方へ';
      const title = document.createElement('h3');
      title.textContent = 'まやのコーヒーカルテ';
      const description = document.createElement('p');
      description.textContent = 'タイプの強みや意外な一面をまとめた詳しいカルテと、「7日間はじめてのコーヒー講座」をLINEでお届けします。';
      const privacy = document.createElement('p');
      privacy.className = 'maya-personality-line__privacy';
      privacy.textContent = 'LINE登録は任意です。診断結果とおすすめの閲覧に必要な情報だけを使用します。';
      const link = document.createElement('a');
      link.href = lineProfile.loginUrl;
      link.textContent = lineProfile.ctaLabel || 'LINEでカルテを受け取る';
      link.addEventListener('click', () => this.track('line_click', this.personalityResult?.type?.id));
      section.append(eyebrow, title, description, link);
      if (lineProfile.fallbackLoginUrl) {
        const fallback = document.createElement('a');
        fallback.className = 'maya-personality-line__fallback';
        fallback.href = lineProfile.fallbackLoginUrl;
        fallback.textContent = 'LINEアプリが開かない場合はこちら';
        fallback.addEventListener('click', () => this.track('line_click', this.personalityResult?.type?.id));
        section.append(fallback);
      }
      section.append(privacy);
      return section;
    }

    async createShareImage(result, characterUrl) {
      if (document.fonts?.ready) await document.fonts.ready;
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1350;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas is unavailable');
      const type = result.type || {};
      const palette = this.sharePalette(type.id);

      const background = context.createLinearGradient(0, 0, 1080, 1350);
      background.addColorStop(0, palette.background);
      background.addColorStop(1, '#f4eadb');
      context.fillStyle = background;
      context.fillRect(0, 0, 1080, 1350);
      this.roundedRect(context, 54, 54, 972, 1242, 32);
      context.fillStyle = 'rgba(255,253,248,.92)';
      context.fill();
      context.strokeStyle = `${palette.accent}55`;
      context.lineWidth = 2;
      context.stroke();

      context.textAlign = 'center';
      context.fillStyle = palette.accent;
      context.font = '700 34px "Yu Gothic", "Hiragino Kaku Gothic ProN", sans-serif';
      context.fillText(type.hidden ? 'AIコーヒー性格診断・特別タイプ' : 'AIコーヒー性格診断', 540, 116);
      context.fillStyle = '#3a271d';
      context.font = '600 25px Arial, sans-serif';
      context.fillText('THE BOOST COFFEE', 540, 160);

      context.beginPath();
      context.arc(540, 430, 225, 0, Math.PI * 2);
      context.fillStyle = palette.soft;
      context.fill();
      context.strokeStyle = `${palette.accent}45`;
      context.stroke();

      if (!characterUrl) throw new Error(`Character asset is unavailable for type: ${type.id || type.name || 'unknown'}`);
      const character = await this.loadShareImage(characterUrl);
      context.drawImage(character, 285, 205, 510, 510);
      if (typeof character.close === 'function') character.close();

      context.fillStyle = palette.accent;
      context.font = '700 22px "Yu Gothic", "Hiragino Kaku Gothic ProN", sans-serif';
      context.fillText(type.hidden ? 'コーヒー性格診断結果・特別タイプ' : 'コーヒー性格診断結果', 540, 724);
      context.fillStyle = '#2f2018';
      context.font = '600 50px "Yu Mincho", "Hiragino Mincho ProN", serif';
      let y = this.drawWrappedText(context, type.name || 'コーヒータイプ', 540, 786, 850, 70, 2);
      context.fillStyle = palette.accent;
      context.font = '700 34px "Yu Gothic", "Hiragino Kaku Gothic ProN", sans-serif';
      y = this.drawWrappedText(context, type.headline || '', 540, y + 18, 820, 42, 2);

      const social = type.social;
      context.textAlign = 'center';
      context.fillStyle = '#3a271d';
      context.font = '700 32px "Yu Gothic", sans-serif';
      this.drawWrappedText(context, social ? `うれしいこと：${social.joy}` : '', 540, 974, 850, 46, 2);
      this.drawWrappedText(context, social ? `大切な時間：${social.moment}` : '', 540, 1084, 850, 46, 2);
      context.fillStyle = palette.accent;
      context.font = '800 48px "Yu Gothic", sans-serif';
      context.fillText('これ、私っぽい？', 540, 1220);
      context.fillStyle = '#5e493c';
      context.font = '600 24px "Yu Gothic", sans-serif';
      context.fillText('あなたも無料で診断 → The Boost Coffee', 540, 1270);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((value) => value ? resolve(value) : reject(new Error('PNG generation failed')), 'image/png');
      });
      return { blob, filename: `the-boost-coffee-${type.id || 'personality'}.png` };
    }

    sharePalette(typeId) {
      const palettes = {
        discoverer: ['#2d6f6b', '#dcefeb', '#f4eee2'],
        researcher: ['#385c88', '#dfe8f2', '#f4eee2'],
        traveler: ['#087b72', '#d9eee9', '#f5eadf'],
        particular: ['#264a78', '#dfe7f0', '#f3eee5'],
        guardian: ['#9b6258', '#f3dfd9', '#f5efe5'],
        director: ['#9a3158', '#f1dce5', '#f4e9e1'],
        host: ['#446c42', '#e1eddd', '#f4ead8'],
        collector: ['#735096', '#e8def1', '#f2eee4'],
        pioneer: ['#263f77', '#dce3f0', '#eee7d8'],
        tuner: ['#22675f', '#dcebe7', '#f2e7d8'],
        storyteller: ['#773948', '#eddee1', '#f2eadf']
      };
      const [accent, soft, background] = palettes[typeId] || ['#7a2a22', '#eadfd1', '#f4eee4'];
      return { accent, soft, background };
    }

    resolveCharacterUrl(type) {
      const normalizedId = String(type?.id || '').trim().toLowerCase();
      if (this.characterAssets[normalizedId]) return this.characterAssets[normalizedId];

      const typeIdsByName = {
        '小さな発見家': 'discoverer',
        '香りの研究家': 'researcher',
        '気分の旅人': 'traveler',
        '静かなこだわり人': 'particular',
        'ひと息の守り人': 'guardian',
        '気分の演出家': 'director',
        'おもてなし上手': 'host',
        'ときめき収集家': 'collector',
        '味わいの先駆者': 'pioneer',
        '一杯の調律師': 'tuner',
        'コーヒーの語り部': 'storyteller'
      };
      const fallbackId = typeIdsByName[String(type?.name || '').trim()];
      return fallbackId ? this.characterAssets[fallbackId] : null;
    }

    async loadShareImage(src) {
      const absoluteUrl = new URL(src, location.href);
      try {
        const response = await fetch(absoluteUrl.href, { mode: 'cors', cache: 'force-cache' });
        if (!response.ok) throw new Error(`Character image request failed: ${response.status}`);
        const blob = await response.blob();
        if (typeof createImageBitmap === 'function') {
          const bitmap = await createImageBitmap(blob);
          if (!bitmap.width || !bitmap.height) throw new Error('Character bitmap has no drawable dimensions');
          return bitmap;
        }

        const objectUrl = URL.createObjectURL(blob);
        try {
          return await this.loadDecodedImage(objectUrl);
        } finally {
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        }
      } catch (error) {
        console.warn('Character bitmap decoding fell back to an image element', error);
        return this.loadDecodedImage(absoluteUrl.href);
      }
    }

    loadDecodedImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        const absoluteUrl = new URL(src, location.href);
        if (absoluteUrl.origin !== location.origin && !String(src).startsWith('blob:')) image.crossOrigin = 'anonymous';
        image.decoding = 'sync';
        image.onload = async () => {
          try {
            if (typeof image.decode === 'function') await image.decode();
            if (!image.naturalWidth || !image.naturalHeight) throw new Error('Character image has no drawable dimensions');
            await new Promise((done) => requestAnimationFrame(done));
            resolve(image);
          } catch (error) {
            reject(error);
          }
        };
        image.onerror = reject;
        image.src = absoluteUrl.href;
      });
    }

    drawWrappedText(context, text, x, y, maxWidth, lineHeight, maxLines) {
      const lines = [];
      let line = '';
      Array.from(String(text || '')).forEach((character) => {
        const next = line + character;
        if (line && context.measureText(next).width > maxWidth) {
          lines.push(line);
          line = character;
        } else {
          line = next;
        }
      });
      if (line) lines.push(line);
      lines.slice(0, maxLines).forEach((item, index) => context.fillText(item, x, y + index * lineHeight));
      return y + Math.min(lines.length, maxLines) * lineHeight;
    }

    roundedRect(context, x, y, width, height, radius) {
      const safeRadius = Math.min(radius, width / 2, height / 2);
      context.beginPath();
      context.roundRect(x, y, width, height, Math.max(0, safeRadius));
    }

    downloadShareImage(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    isAppleMobile() {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    canShareFile(file) {
      if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
      try {
        return navigator.canShare({ files: [file] });
      } catch (_) {
        return false;
      }
    }

    openShareImage(blob) {
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, '_blank');
      if (opened) opened.opener = null;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }

    renderProductInside(container, product) {
      const card = document.createElement('div');
      card.className = 'maya-profile-product';
      const eyebrow = document.createElement('p');
      eyebrow.textContent = 'まやが選ぶ、今の一杯';
      const title = document.createElement('h3');
      title.textContent = product.title;
      const reason = document.createElement('p');
      reason.textContent = [product.reason, product.price].filter(Boolean).join('　');
      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = product.ctaLabel || 'この一杯を見てみる';
      link.addEventListener('click', () => this.track('product_click', this.personalityResult?.type?.id));
      card.append(eyebrow, title, reason, link);
      container.append(card);
    }

    async submit(rawMessage) {
      const message = rawMessage.trim().slice(0, 400);
      if (!message || this.busy) return;
      if (/^(?:コーヒー)?性格診断(?:を)?(?:始めたい|試したい|やりたい)$/.test(message)) {
        this.history = [];
        this.recommendation.replaceChildren();
        this.recommendation.hidden = true;
        this.subscriptionOffer.hidden = true;
        this.chooseMode('personality');
        return;
      }
      try {
        await this.ensureSession();
      } catch (_) {
        this.setReply('ごめんなさい、いま通信がうまくつながりませんでした。もう一度だけ試してもらえますか？');
        return;
      }

      this.setBusy(true);
      this.input.value = '';
      if (this.mode === null) {
        this.mode = 'chat';
        this.personalityComplete = true;
        this.choices.classList.remove('maya-choices--mode');
        if (this.chatIntro) this.chatIntro.hidden = true;
      }
      this.setReply('少しお待ちくださいね♪');
      const replyBounds = this.reply.getBoundingClientRect();
      if (replyBounds.top < 100 || replyBounds.top > window.innerHeight - 100) {
        this.reply.scrollIntoView({ block: 'center', behavior: 'instant' });
      }

      try {
        const data = await this.request(this.chatEndpoint, {
          sessionToken: this.sessionToken,
          message,
          history: this.history.slice(-MAX_HISTORY),
          seenProductHandles: [...this.seenProductHandles],
          currentProductHandle: this.currentProductHandle,
          personalityResult: this.personalityResult ? {
            typeId: this.personalityResult.type?.id,
            axes: this.personalityResult.axes
          } : null
        });

        this.setReply(data.reply);
        this.rememberProduct(data.recommendation);
        this.history.push({ role: 'user', text: message });
        const historyReply = data.recommendation?.title
          ? `${data.reply}\n[表示した商品: ${data.recommendation.title}]`
          : data.reply;
        this.history.push({ role: 'model', text: historyReply });
        this.history = this.history.slice(-MAX_HISTORY);
        this.renderProgress(data.progress);
        this.renderChoices(data.quickReplies || []);
        if (this.personalityResult && this.recommendation.querySelector('.maya-profile-result')) {
          if (data.recommendation) {
            this.recommendation.querySelectorAll('.maya-profile-product').forEach(card => card.remove());
            this.renderProductInside(this.recommendation, data.recommendation);
          }
        } else if (!data.preservePersonalityResult) {
          this.renderRecommendation(data.recommendation || null, data.lineProfile || null);
        }
        this.renderSubscriptionOffer(data.subscriptionOffer || null);
      } catch (_) {
        this.input.value = message;
        this.setReply('ごめんなさい、いま通信がうまくつながりませんでした。もう一度だけ試してもらえますか？');
      } finally {
        this.setBusy(false);
      }
    }

    async request(url, body, retried = false) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal
        });
        if (response.status === 401 && body.sessionToken && !retried) {
          this.sessionToken = null;
          await this.ensureSession();
          return this.request(url, { ...body, sessionToken: this.sessionToken }, true);
        }
        if (!response.ok) {
          const error = new Error(`HTTP ${response.status}`);
          error.status = response.status;
          throw error;
        }
        return await response.json();
      } finally {
        clearTimeout(timer);
      }
    }

    renderProgress(progress = {}) {
      this.progress.hidden = false;
      const total = Math.max(1, Number(progress.total) || 4);
      const current = Math.min(total, Math.max(1, Number(progress.current) || 1));
      this.root.querySelector('[data-maya-progress-current]').textContent = current;
      this.root.querySelector('[data-maya-progress-total]').textContent = total;
      this.root.querySelector('[data-maya-progress-label]').textContent = progress.label || '好みを探しています';
      this.root.querySelector('[data-maya-progress-bar]').style.width = `${(current / total) * 100}%`;
    }

    renderChoices(items) {
      this.choices.replaceChildren();
      items.slice(0, 4).forEach((item) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.value = String(item).slice(0, 80);
        button.textContent = String(item).slice(0, 80);
        this.choices.append(button);
      });
    }

    renderRecommendation(product, lineProfile = null) {
      this.recommendation.classList.remove('maya-recommendation--profile');
      this.recommendation.replaceChildren();
      this.recommendation.hidden = !product;
      if (!product) return;

      if (product.imageUrl) {
        const image = document.createElement('img');
        image.src = product.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        this.recommendation.append(image);
      }

      const body = document.createElement('div');
      const title = document.createElement('h2');
      title.textContent = product.title || 'おすすめの商品';
      const reason = document.createElement('p');
      const price = product.price
        ? (/^¥/.test(String(product.price)) ? String(product.price) : `¥${Number(product.price).toLocaleString('ja-JP')}`)
        : '';
      reason.textContent = [product.reason, price].filter(Boolean).join('　');
      const link = document.createElement('a');
      link.href = product.url || '/collections/all';
      link.textContent = product.ctaLabel || (product.available === false ? '商品ページで確認する' : 'この豆を見てみる');
      body.append(title, reason, link);
      if (lineProfile?.loginUrl) {
        const lineBox = document.createElement('div');
        lineBox.className = 'maya-line-profile';
        const lineLabel = document.createElement('p');
        lineLabel.textContent = '診断結果はここで終了できます。残しておきたい方だけ、LINEへ保存できます。';
        const lineLink = document.createElement('a');
        lineLink.className = 'maya-line-profile__link';
        lineLink.href = lineProfile.loginUrl;
        lineLink.textContent = lineProfile.ctaLabel || 'LINEでカルテを受け取る';
        lineBox.append(lineLabel, lineLink);
        body.append(lineBox);
      }
      this.recommendation.append(body);
    }

    renderSubscriptionOffer(offer) {
      this.subscriptionOffer.replaceChildren();
      this.subscriptionOffer.hidden = !offer;
      if (!offer) return;

      const eyebrow = document.createElement('p');
      eyebrow.className = 'maya-subscription-offer__eyebrow';
      eyebrow.textContent = offer.eyebrow || 'サブスクのご案内';

      const title = document.createElement('h3');
      title.textContent = offer.title || 'コーヒーサブスク';

      const description = document.createElement('p');
      description.className = 'maya-subscription-offer__description';
      description.textContent = offer.description || '';

      const price = document.createElement('p');
      price.className = 'maya-subscription-offer__price';
      price.textContent = offer.price || '';
      price.hidden = !offer.price;

      const link = document.createElement('a');
      link.href = offer.url || '/products/coffee-subscription';
      link.textContent = offer.ctaLabel || 'サブスクを見る';
      link.addEventListener('click', () => this.track('subscription_click', this.personalityResult?.type?.id));

      const note = document.createElement('p');
      note.className = 'maya-subscription-offer__note';
      note.textContent = offer.note || '';
      note.hidden = !offer.note;

      this.subscriptionOffer.append(eyebrow, title, description, price, link, note);
    }

    setReply(text) {
      this.reply.textContent = String(text || '').slice(0, 1200);
    }

    rememberProduct(product) {
      if (!product?.url) return;
      try {
        const handle = new URL(product.url, location.href).pathname.match(/^\/products\/([^/]+)/)?.[1];
        if (handle) {
          this.seenProductHandles.add(handle);
          this.currentProductHandle = decodeURIComponent(handle);
        }
      } catch (_) { /* A missing product link must not interrupt the result. */ }
    }

    setBusy(value) {
      this.busy = value;
      this.root.querySelector('[data-maya-restart]').disabled = value;
      this.root.setAttribute('aria-busy', String(value));
      this.input.disabled = value;
      this.send.disabled = value;
      this.choices.querySelectorAll('button').forEach((button) => { button.disabled = value; });
    }

    async reset() {
      if (this.busy) return;
      this.seenProductHandles.clear();
      this.currentProductHandle = null;
      if (this.shareObjectUrl) {
        URL.revokeObjectURL(this.shareObjectUrl);
        this.shareObjectUrl = null;
      }
      this.history = [];
      this.answers = [];
      this.questionIndex = 0;
      this.mode = null;
      this.personalityComplete = false;
      this.personalityResult = null;
      this.sessionToken = null;
      this.reply.textContent = this.initialReply;
      this.recommendation.hidden = true;
      this.recommendation.classList.remove('maya-recommendation--profile');
      this.recommendation.replaceChildren();
      this.subscriptionOffer.hidden = true;
      this.subscriptionOffer.replaceChildren();
      this.form.hidden = true;
      this.progress.hidden = true;
      await this.startSession();
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-maya-barista]:not([data-maya-mounted])').forEach((root) => {
      root.dataset.mayaMounted = 'true';
      new MayaBarista(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => mount());
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
